import {
    CSSProperties,
    FC,
    PropsWithChildren,
    ReactElement,
    useEffect,
    useRef,
    useState,
} from 'react'
import { createPortal } from 'react-dom'
import classNames from 'classnames'

import { appRootId } from '../../../index'

import './PacoModal.scss'

export interface PacoModalProps extends PropsWithChildren {
    isWide?: boolean;
    transitionDuration?: number;
    onModalClick?: () => void;
    onClose?: () => void;
    className?: string;
    cardClassName?: string;
}

const PacoModal: FC<PacoModalProps> = ({
    isWide,
    transitionDuration = 200,
    onModalClick,
    onClose,
    className = '',
    cardClassName = '',
    children,
}): ReactElement => {
    const [isOpened, setIsOpened] = useState<boolean>(false)

    const closeButtonRef = useRef<HTMLButtonElement>(null)
    const [rootElement] = useState<Element | null>(document.getElementById(appRootId))

    useEffect((): void => {
        setIsOpened(true)
    }, [])

    useEffect((): () => void => {
        if (isOpened) document.body.classList.add('has-active-modal')

        if (isOpened && closeButtonRef.current) {
            closeButtonRef.current.focus()
            closeButtonRef.current.blur()
        }

        return (): void => document.body.classList.remove('has-active-modal')
    }, [isOpened])

    useEffect((): () => void => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape') {
                setIsOpened(false)

                if (onClose) {
                    onClose()
                }
            }
        }

        window.addEventListener('keydown', handleKeyDown)

        return (): void => window.removeEventListener('keydown', handleKeyDown)
    }, [onClose])

    const cssVariables = {
        '--modal-transition-duration': `${transitionDuration}ms`,
    } as CSSProperties

    const modalClassNames = classNames('paco-modal', {
        'paco-modal--is-opened': isOpened,
        'paco-modal--is-wide': isWide,
    }, className)

    const modalContent = (
        <section
            style={cssVariables}
            role="none"
            onClick={onModalClick}
            className={modalClassNames}
        >
            <div className={`paco-modal__card ${cardClassName}`}>
                {children}
            </div>
        </section>
    )

    if (rootElement) {
        return createPortal(
            modalContent,
            rootElement,
        )
    }

    return modalContent
}

export default PacoModal
